import { Flame, Phone, Building2, Target, Copy } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useThemeStore } from '@/stores/themeStore'
import type { BdrLead } from '@/types'
import type { Cadencia } from '@/types'
import { faseInfo, decisorBadge, panelSurface, insetSurface, labelCaps, bodyMuted } from './bdrLeadDetailsTheme'

export function BdrLeadDetailsIdentity({
  selectedLead,
  cadencia,
}: {
  selectedLead: BdrLead
  cadencia: Cadencia | null
}) {
  const isLightTheme = useThemeStore(s => s.theme.mode === 'light')
  const currentFase = cadencia?.fase ?? 1
  const currentStep = cadencia?.tentativa ?? 0
  const initials = (selectedLead.contato || selectedLead.empresa || '??').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const score = selectedLead.score ?? 0
  const scoreColor = score >= 70 ? 'text-red-700 dark:text-red-400' : score >= 40 ? 'text-amber-800 dark:text-amber-400' : 'text-teal-800 dark:text-teal-400'
  const scoreLabel = score >= 70 ? 'Hot' : score >= 40 ? 'Warm' : 'Cold'
  const fase = faseInfo[currentFase] ?? faseInfo[1]
  const faseBadgeLabel = fase.label

  return (
    <>
        {/* Identity Card */}
        <div className={cn(panelSurface, 'p-5')}>
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shrink-0 border-2"
              style={{ backgroundColor: fase.fill, color: fase.text, borderColor: fase.border }}
            >
              {initials}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-zinc-900 dark:text-text-primary font-bold text-base truncate">
                  {selectedLead.empresa}
                </h3>
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-black border-2"
                  style={{
                    backgroundColor: fase.fill,
                    color: fase.text,
                    borderColor: fase.border,
                  }}
                >
                  {faseBadgeLabel}
                </span>
              </div>
              <p className={cn(bodyMuted, 'text-sm mt-0.5 font-semibold')}>{selectedLead.contato}</p>
              <div className="flex items-center gap-2 mt-1">
                {selectedLead.cargo && (
                  <p className={cn(bodyMuted, 'text-xs font-medium')}>{selectedLead.cargo}</p>
                )}
                {selectedLead.decisor && (
                  <span
                    className="px-2.5 py-1 rounded-md border-2 text-[11px] font-black uppercase tracking-wide shrink-0"
                    style={{
                      backgroundColor: isLightTheme
                        ? decisorBadge.light.bg
                        : decisorBadge.dark.bg,
                      color: isLightTheme ? decisorBadge.light.text : decisorBadge.dark.text,
                      borderColor: isLightTheme
                        ? decisorBadge.light.border
                        : decisorBadge.dark.border,
                    }}
                  >
                    Decisor
                  </span>
                )}
              </div>
            </div>

            {/* Score */}
            <div className="flex flex-col items-center shrink-0">
              <div className={cn('flex items-center gap-1 text-xl font-black', scoreColor)}>
                <Flame size={18} />
                {score}
              </div>
              <span className={cn('text-[10px] font-black uppercase tracking-wider', bodyMuted)}>
                {scoreLabel}
              </span>
            </div>
          </div>

          {/* Cadence Progress Bar */}
          <div className={cn('mt-5 p-4', insetSurface)}>
            <div className="flex items-center justify-between mb-3">
              <span className={labelCaps}>Progresso da Cadência</span>
              <span className="text-xs font-black text-zinc-900 dark:text-text-primary tabular-nums">
                {currentStep}/15 tentativas
              </span>
            </div>
            <div
              className="rounded-full border border-slate-200/90 bg-[#f1f5f9] p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] dark:border-slate-600/60 dark:bg-slate-800/40"
              role="progressbar"
              aria-valuenow={currentStep}
              aria-valuemin={0}
              aria-valuemax={15}
              aria-label="Progresso da cadência"
            >
              <div className="flex h-3 gap-0.5">
                {Array.from({ length: 15 }, (_, i) => {
                  const stepNum = i + 1
                  const isCompleted = stepNum <= currentStep
                  const isCurrent = stepNum === currentStep + 1
                  const phase =
                    stepNum <= 5
                      ? faseInfo[1]
                      : stepNum <= 10
                        ? faseInfo[2]
                        : stepNum <= 12
                          ? faseInfo[3]
                          : faseInfo[4]

                  return (
                    <div
                      key={i}
                      className={cn(
                        'flex-1 rounded-full transition-all duration-500 ease-out',
                        isCurrent && 'animate-pulse shadow-sm',
                        !isCompleted && 'bg-white/70 dark:bg-slate-700/50',
                        isCompleted && 'opacity-100',
                        !isCompleted && !isCurrent && 'opacity-90',
                      )}
                      style={{
                        backgroundColor: isCompleted || isCurrent ? phase.progress : undefined,
                        ...(isCurrent
                          ? {
                              boxShadow: `0 0 0 1px ${phase.border}40, 0 0 8px ${phase.progress}80`,
                            }
                          : {}),
                      }}
                    />
                  )
                })}
              </div>
            </div>
            <div className="flex justify-between mt-2.5 gap-2">
              {[faseInfo[1], faseInfo[2], faseInfo[3], faseInfo[4]].map((ph, idx) => (
                <div key={ph.label} className="flex flex-col items-center min-w-0">
                  <span className="text-xs font-black" style={{ color: ph.text }}>
                    F{idx + 1}
                  </span>
                  <span className="text-[10px] font-bold text-zinc-600 dark:text-text-muted">
                    {idx === 0 ? '1–5' : idx === 1 ? '6–10' : idx === 2 ? '11–12' : '13–15'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Contact Info & Qualification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={cn(panelSurface, 'p-4 flex items-center gap-3')}>
            <div className="p-2.5 bg-indigo-100 border-2 border-indigo-300 rounded-xl text-indigo-800 shrink-0 dark:bg-accent/15 dark:border-transparent dark:text-accent">
              <Phone size={18} strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={labelCaps}>Telefone</p>
              <p className="text-sm font-black text-zinc-900 dark:text-text-primary truncate mt-0.5">
                {selectedLead.telefone}
              </p>
            </div>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(selectedLead.telefone)
              }}
              className="p-2 hover:bg-zinc-100 dark:hover:bg-white/10 rounded-lg text-zinc-700 dark:text-text-secondary transition-colors border border-zinc-200 dark:border-transparent"
            >
              <Copy size={14} />
            </button>
          </div>

          <div className={cn(panelSurface, 'p-4 flex items-center gap-3')}>
            <div className="p-2.5 bg-emerald-100 border-2 border-emerald-300 rounded-xl text-emerald-800 shrink-0 dark:bg-emerald-500/15 dark:border-transparent dark:text-emerald-400">
              <Building2 size={18} strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={labelCaps}>Budget</p>
              <p className="text-sm font-black text-zinc-900 dark:text-text-primary truncate mt-0.5">
                {selectedLead.budget || 'Não inf.'}
              </p>
            </div>
          </div>

          {(selectedLead.dores || selectedLead.problema) && (
            <div className={cn(panelSurface, 'p-4 col-span-full')}>
              <div className="flex items-center gap-2 mb-2">
                <Target size={14} className="text-accent" />
                <span className={labelCaps}>Dores & Problemas</span>
              </div>
              <p className="text-sm text-text-primary leading-relaxed">
                {selectedLead.dores || selectedLead.problema}
              </p>
            </div>
          )}
        </div>

    </>
  )
}
